<template>
  <div
    @keyup.esc="close"
    @keyup.enter="next"
    @keyup.left="prev"
    @keyup.right="next"
    @keyup.esc.prevent="$emit('close')"
    class="relative flex flex-col justify-center w-full space-y-6"
  >
    <div
      @click="showTooltip = !showTooltip"
      class="flex flex-row space-x-2 text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
    >
      <h2>{{ item.questionDisplay }}</h2>
      <span v-if="item.required" class="text-ruby">*</span>
      <span class="cursor-pointer" v-if="item.tooltip.length"
        ><InformationCircleIcon class="w-8 h-8 text-gold"
      /></span>
    </div>
    <label class="text-sm text-navada">{{ item.placeholder }} </label>
    <form
      class="w-full space-y-6"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
    >
      <RadioGroup v-model="selected">
        <RadioGroupLabel class="sr-only">
          {{ item.questionDisplay }}
        </RadioGroupLabel>
        <div class="space-y-4">
          <RadioGroupOption
            v-focus
            as="template"
            v-for="(choice, idx) in item.choices"
            :key="idx"
            :value="idx"
            v-slot="{ active, checked }"
          >
            <div
              :class="[
                active ? '' : '',
                'relative md:w-full block rounded-lg border border-gray-300 bg-white dark:bg-[#1b1c20] shadow-sm px-6 py-4 cursor-pointer hover:border-[#1b1c20] dark:hover:border-gold sm:flex sm:justify-between focus:outline-none',
              ]"
            >
              <div class="flex items-center">
                <div class="text-sm">
                  <RadioGroupLabel
                    as="p"
                    class="text-xl font-bold text-gray-900 dark:text-white"
                  >
                    {{ choice }}
                  </RadioGroupLabel>
                </div>
              </div>

              <div
                :class="[
                  checked
                    ? 'border-[#1b1c20] dark:border-gold'
                    : 'border-transparent',
                  'absolute -inset-px rounded-lg border-[4px] pointer-events-none',
                ]"
                aria-hidden="true"
              />
            </div>
          </RadioGroupOption>
        </div>
      </RadioGroup>
      <!-- Desktop action -->
      <div
        class="flex-col justify-between hidden w-full pt-4 md:flex md:w-full md:flex-row"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <div class="flex item-center space-x-6">
          <button
            v-if="!item.required"
            @click="$emit('skip', item, selected)"
            class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white">{{
              t("input.skip")
            }}</span>
            <SkipIcon />
          </button>
          <button
            @click="$emit('close')"
            class="inline-flex items-center justify-center space-x-1 font-bold text-sm py-2 text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white"></span>
            <BackIcon />
          </button>
        </div>
      </div>
      <!-- Mobile action -->
      <div class="flex flex-col w-full pt-4 space-y-4 md:hidden">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 w-full min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
          <div class="flex item-center space-x-6">
            <button
              v-if="!item.required"
              @click="$emit('skip', item, selected)"
              class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
            >
              <span class="text-black dark:text-white">{{
                t("input.skip")
              }}</span>
              <SkipIcon />
            </button>
            <button
              @click="$emit('close')"
              class="inline-flex items-center justify-center space-x-1 font-bold text-sm py-2 text-white rounded-full focus:outline-none"
            >
              <span class="text-black dark:text-white"></span>
              <BackIcon />
            </button>
          </div>
        </div>
      </div>
    </form>

    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { computed, reactive, toRefs } from "vue";
import {
  RadioGroup,
  RadioGroupDescription,
  RadioGroupLabel,
  RadioGroupOption,
} from "@headlessui/vue";
import Spinner from "../Spinner";
import { useI18n } from "vue-i18n";
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/solid";
import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";
import BackIcon from "../icons/BackIcon";

export default {
  name: "Radio",
  components: {
    SkipIcon,
    BackIcon,
    ReturnIcon,
    InformationCircleIcon,
    ExclamationCircleIcon,
    Spinner,
    RadioGroup,
    RadioGroupDescription,
    RadioGroupLabel,
    RadioGroupOption,
  },
  props: {
    applications: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: {
        placeholder: "",
        questionDisplay: "",
        tooltip: "",
        choices: [],
        required: false,
      },
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const answer = computed(() => props.item.answer);
    const state = reactive({
      showTooltip: false,
      showError: false,
      error: "",
      selected: answer.value ? answer.value.answer : "",
    });
    const next = () => {
      if (
        props.item.required &&
        (state.selected === null ||
          state.selected === "" ||
          state.selected.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }
      emit("next", props.item, state.selected);
    };
    const prev = () => {
      emit("prev");
    };
    const close = () => {
      emit("close");
    };
    return {
      t,
      next,
      close,
      prev,
      ...toRefs(state),
    };
  },
};
</script>
